import { REPORT_CANVAS_MAX_COMPONENTS } from '@erp/rpc';
import { REPORT_CANVAS_COLUMNS } from '@erp/shared-types';
import { bottomSpot, clampRect, compactRects, gridBottom } from './grid';
import {
  componentSize,
  type ComponentRegistry,
  type ComponentSize,
} from './registry';
import type {
  GridLayout,
  GridRect,
  LayoutItem,
  ReportComponentConfig,
} from './types';

// ── Node IDs ──────────────────────────────────────────────────────────────────

/**
 * Random node id. Layouts are persisted and rehydrated across sessions, so a
 * module-level counter would collide with ids already present in a loaded
 * layout; a random suffix cannot.
 */
export function newNodeId() {
  return `rn-${crypto.randomUUID().slice(0, 8)}`;
}

// ── Size caps ─────────────────────────────────────────────────────────────────

/**
 * True when the layout exceeds the persistence schema's size cap
 * (ReportCanvasLayoutSchema rejects such layouts at save time). Hosts check
 * candidate layouts against this and refuse cap-exceeding adds, so every
 * layout a user can build is guaranteed saveable.
 */
export function exceedsLayoutCaps(layout: GridLayout): boolean {
  return layout.length > REPORT_CANVAS_MAX_COMPONENTS;
}

// ── Grid layout helpers ───────────────────────────────────────────────────────
// All pure: layout in, new compacted layout out (see `compactRects`). No
// React, DnD, or data dependencies.

/** A new item whose component config comes from a registry entry's factory, at its default size. */
export function makeItem(
  make: (id: string) => ReportComponentConfig,
  size: Pick<ComponentSize, 'w' | 'h'>,
): LayoutItem {
  const id = newNodeId();
  return { id, x: 0, y: 0, w: size.w, h: size.h, component: make(id) };
}

/** `layout` with the grid compacted upward (see `compactRects`). */
export function compactLayout(layout: GridLayout, first?: string): GridLayout {
  return compactRects(layout.map(clampRect), first);
}

/** Adds `item` in the first free spot at the bottom of the grid. */
export function addItem(layout: GridLayout, item: LayoutItem): GridLayout {
  return compactLayout([...layout, { ...item, ...bottomSpot(layout, item) }]);
}

/**
 * Adds a new component of `type` at the bottom (see {@link addItem}), at its
 * type's size; the layout unchanged when the registry lacks the type or the
 * layout is full. Returns the new item's id with it.
 */
export function addComponent(
  layout: GridLayout,
  registry: ComponentRegistry,
  type: string,
): { layout: GridLayout; id: string | null } {
  const definition = registry[type];
  if (!definition) return { layout, id: null };
  const item = makeItem(definition.make, componentSize(registry, type));
  const next = addItem(layout, item);
  return exceedsLayoutCaps(next)
    ? { layout, id: null }
    : { layout: next, id: item.id };
}

/** Adds `item` at `at` (a drop), pushing what is there down. */
export function addItemAt(
  layout: GridLayout,
  item: LayoutItem,
  at: { x: number; y: number },
): GridLayout {
  return compactLayout([...layout, { ...item, ...at }], item.id);
}

export function removeItem(layout: GridLayout, id: string): GridLayout {
  return compactLayout(layout.filter((item) => item.id !== id));
}

export function updateItem(
  layout: GridLayout,
  id: string,
  component: ReportComponentConfig,
): GridLayout {
  return layout.map((item) => (item.id === id ? { ...item, component } : item));
}

/** Moves an item to `to`; items there are pushed down, then all rise. */
export function moveItem(
  layout: GridLayout,
  id: string,
  to: { x: number; y: number },
): GridLayout {
  return compactLayout(
    layout.map((item) =>
      item.id === id ? { ...item, x: to.x, y: to.y } : item,
    ),
    id,
  );
}

/**
 * Resizes an item to `size`, no smaller than `min` and within the grid's
 * columns from where it sits; items it now covers are pushed down.
 */
export function resizeItem(
  layout: GridLayout,
  id: string,
  size: { w: number; h: number },
  min: { minW: number; minH: number },
): GridLayout {
  return compactLayout(
    layout.map((item) =>
      item.id === id
        ? {
            ...item,
            w: Math.min(
              REPORT_CANVAS_COLUMNS - item.x,
              Math.max(min.minW, Math.round(size.w)),
            ),
            h: Math.max(min.minH, Math.round(size.h)),
          }
        : item,
    ),
    id,
  );
}

function placeOf(layout: GridLayout, id: string): GridRect | undefined {
  return layout.find((item) => item.id === id);
}

/**
 * Moves an item one step (the keyboard's arrows): a column left or right, or
 * up or down past the next thing in its way — compaction would otherwise
 * float a one-row nudge straight back. Unchanged at the grid's edge.
 */
export function nudgeItem(
  layout: GridLayout,
  id: string,
  direction: 'left' | 'right' | 'up' | 'down',
): GridLayout {
  const from = placeOf(layout, id);
  if (!from) return layout;
  if (direction === 'left' || direction === 'right')
    return moveItem(layout, id, {
      x: from.x + (direction === 'right' ? 1 : -1),
      y: from.y,
    });
  const step = direction === 'down' ? 1 : -1;
  const bottom = gridBottom(layout);
  for (let y = from.y + step; y >= 0 && y <= bottom; y += step) {
    const next = moveItem(layout, id, { x: from.x, y });
    const place = placeOf(next, id);
    if (place && place.y !== from.y) return next;
  }
  return layout;
}
